import { IClientData } from '../../../../Assembler/dist/registry/iface/iClientData.js';
import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import type { GetParentJsonValueHandler } from '../../../../Assembler/dist/registry/iface/getParentJsonValueHandler.js';
import type { ITransientStore } from '../../../../Assembler/dist/registry/iface/iTransientStore.js';
import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import { TimeFilter } from '../../registryCommon/dynamicData/timeFilter.js';

/**
 * Purpose:
 * Server-side CLIENT-DATA preparer for the time capability. Produces the RAW island — the instant already
 * resolved server-side plus the DateFormat to apply — that a browser-side handler will later format and
 * render WITHOUT a server round-trip.
 *
 * Responsibilities:
 * - Read the DateFormat / IsLocalTime / FixedTime params by REUSING TimeFilter
 * - Resolve the instant ONCE, server-side, and ship it in the invariant wire format
 * - Leave the FORMATTING to the client processor; the preparer never renders the display string
 *
 * The instant is resolved here rather than in the browser on purpose: the browser clock is not the render
 * clock, so a client-resolved instant would drift from the server render and from every other language.
 * With FixedTime declared the two tiers are byte-identical by construction.
 *
 * This lives in the SAME registry root as TimeData — the preparer belongs beside the data provider whose
 * output it mirrors. It never runs on the pure server IData path: the applyDynamicClientData merge step
 * invokes it for components bound in dynamicclientdata.json.
 */
export class TimeClientData extends IClientData {
    /**
     * Gets the full type name for DynamicClientData operation tracking.
     */
    get typeName(): string {
        return 'TimeClientData';
    }

    /**
     * Produces the raw time island in a single server-side pass. Gated on the component expecting
     * CurrentTime, exactly as TimeData is, so the server and client tiers agree on WHICH components carry
     * a time as well as on what it renders.
     */
    getClientData(appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, transientStore: ITransientStore, configJson: JsonObject | null = null, getParentJsonValue: GetParentJsonValueHandler | null = null): JsonObject {
        void appSite; void appView; void appFile; void componentDepth; void resolvedAppSite; void resolvedComponentName; void transientStore; void configJson; void getParentJsonValue;
        const rawDataJson = new JsonObject();

        try {
            if (componentJson === null || componentJson === undefined || componentJson.containsKey(TimeFilter.readTimeKey(paramJson)) === false) {
                return rawDataJson;
            }

            // REUSE the shared producer — no duplicated param reads or resolution logic.
            const dateFormat = TimeFilter.readDateFormat(paramJson);
            const isLocalTime = TimeFilter.readIsLocalTime(paramJson);
            const fixedTime = TimeFilter.readFixedTime(paramJson);

            const instant = TimeFilter.resolveCurrentTime(fixedTime, isLocalTime);
            const rawTimeJson = TimeFilter.buildRawTime(instant, dateFormat);

            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`TimeClientData.GetClientData: Returned raw instant for ${componentName}`, 'TimeClientData');
            }

            return rawTimeJson;
        } catch (ex) {
            Logger.error((ex as Error).message, 'TimeClientData.GetClientData');
        }

        return rawDataJson;
    }
}
